import { Component, signal, inject, WritableSignal, Signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { UserService } from 'src/app/shared/services/user.service';
import { combineLatest, firstValueFrom, iif, of, switchMap } from 'rxjs';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Role, User } from '../user.model';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import { UserCreateRequest, UserEditRequest } from 'src/app/shared/services/user.service';
import { Organization } from '../../organization/organization-models';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { DepartmentRestrictionModalComponent } from '../department-restriction-modal/department-restriction-modal.component';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';

interface IUserCreateOrEditForm {
  organizationId: FormControl<number | null>;
  identifer: FormControl<string | null>;
  emailAddress: FormControl<string | null>;
  name: FormControl<string | null>;
  selectedRole: FormControl<Role | null>;
  organization: FormControl<Organization | null>;
}

@Component({
  selector: 'adm-user-create-or-edit-modal',
  templateUrl: './user-create-or-edit-modal.component.html',
  styleUrls: ['./user-create-or-edit-modal.component.scss'],
  imports: [
    AdmOrgTypeaheadComponent,
    FeedbackInfoComponent,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatHint,
    MatInput,
    MatRadioGroup,
    MatRadioButton,
  ],
})
export class UserCreateOrEditModalComponent {
  dialogRef = inject<MatDialogRef<UserCreateOrEditModalComponent>>(MatDialogRef);
  data = inject<{
    organizationId: number;
    userId: number;
  }>(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private snackbarService = inject(SnackbarService);
  resetOrgTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });

  public isEdit = this.data?.userId !== undefined;
  public userRoles: Role[] = [];
  public organizations = signal<Organization[]>([]);
  public organizations$ = toObservable(this.organizations);
  public saving = false;

  public userFormGroup = new FormGroup<IUserCreateOrEditForm>({
    organizationId: new FormControl<number | null>(null),
    organization: new FormControl<Organization | null>(null),
    identifer: new FormControl<string | null>(null),
    emailAddress: new FormControl<string | null>(null, [Validators.required]),
    name: new FormControl<string | null>(null, [Validators.required]),
    selectedRole: new FormControl<Role | null>(null, [Validators.required]),
  });

  get selectedRole() {
    return this.userFormGroup.controls.selectedRole.value;
  }

  get isOrganizationValid() {
    return this.userFormGroup.controls.organization.value?.id;
  }
  private dataSource$ = combineLatest([
    this.userService.getRoles(),
    iif(() => this.data?.userId !== undefined && this.data.userId !== null, this.userService.getUser(this.data.userId), of(null)),
  ]).pipe(
    switchMap(([roles, user]) => {
      this.userRoles = roles;
      const defaultRole = this.userRoles[0] ?? null;
      if (user) {
        this.userFormGroup.patchValue({
          organization: <Organization>{ id: user.organizationId, name: user.organizationName },
          emailAddress: user.email,
          name: user.firstName + ' ' + user.lastName,
        });
        this.userFormGroup.controls.identifer.disable();
        this.userFormGroup.controls.organization.disable();
        this.userFormGroup.controls.organizationId.disable();
        this.userFormGroup.controls.emailAddress.disable();
        if (user.rolesAsStrings.length > 0) {
          const selectedRole = this.userRoles.find(u => u.name === user.rolesAsStrings[0]);
          this.userFormGroup.controls.selectedRole.setValue(selectedRole ?? null);
        }
      }
      if (!this.userFormGroup.controls.selectedRole.value) {
        this.userFormGroup.controls.selectedRole.setValue(defaultRole);
      }
      return of(user);
    })
  );

  user: Signal<User | null> = toSignal(this.dataSource$, { initialValue: null });

  organizationSelected(org: Organization | null) {
    this.userFormGroup.patchValue({
      organizationId: org ? org.id : null,
      organization: org,
    });
  }

  public resetOrganization() {
    this.userFormGroup.patchValue({
      organizationId: null,
    });
  }

  public async saveUser() {
    this.saving = !this.userFormGroup.invalid;
    if (!this.saving) {
      return;
    }

    const existingUser = this.user();
    const userId = existingUser?.id;
    const user: User = existingUser ?? ({} as User);
    const name = (this.userFormGroup.controls.name.value as string).trim();

    user.email = (this.userFormGroup.controls.emailAddress.value as string).trim();
    const spaceBeforeLastName = name.lastIndexOf(' ');
    user.firstName = name.substring(0, spaceBeforeLastName).trim();
    user.lastName = name.substring(spaceBeforeLastName, name.length).trim();

    if (userId) {
      this.editUser(user);
    } else {
      this.createUser(user);
    }
  }

  private editUser(user: User) {
    const { organizationId: _organizationId, ...userData } = user;
    const newRole = this.userFormGroup.controls.selectedRole.value as Role;

    const editUser: UserEditRequest = {
      ...userData,
      rolesToRemove: this.userRoles.filter(ur => ur.id !== newRole.id),
      roles: [newRole],
    };

    this.userService
      .edit(editUser)
      .subscribe({
        next: async () => {
          this.dialogRef.close({ ...user, ...editUser });
        },
        error: () => {
          this.snackbarService.error('Noget gik galt og brugeren blev ikke gemt.');
        },
      })
      .add(() => {
        this.saving = false;
      });
  }

  private createUser(user: User) {
    const selectedOrganization = this.userFormGroup.controls.organization.value;
    if (!selectedOrganization?.id) {
      this.saving = false;
      return;
    }

    const role = this.userFormGroup.controls['selectedRole'].value as Role;
    const createRequest: UserCreateRequest = {
      organizationId: selectedOrganization.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role,
    };

    this.userService
      .create(createRequest)
      .subscribe({
        next: async userId => {
          user = await firstValueFrom(this.userService.getUser(userId));

          const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
              title: 'Bestem afdelingsbegrænsning',
              text: `Vil du begrænse de afdelinger ${user?.firstName} ${user?.lastName} må se data for nu?`,
              confirmText: 'Ja',
              denyText: 'Nej',
            },
            disableClose: true,
          });

          confirmDialogRef.afterClosed().subscribe(confirmed => {
            if (confirmed) {
              const confirmDialogRef = this.dialog.open(DepartmentRestrictionModalComponent, {
                data: {
                  user: user,
                },
                disableClose: true,
                width: '1024px',
              });

              confirmDialogRef.afterClosed().subscribe(updatedUser => {
                if (updatedUser) {
                  this.dialogRef.close(user);
                }
              });
            } else {
              this.dialogRef.close(user);
            }
          });
        },
        error: (err: any) => {
          if (err?.error.code === 100711) {
            this.snackbarService.error('Der findes allerede en bruger med det brugernavn (e-mail adresse).');
          } else {
            this.snackbarService.error('Noget gik galt og brugeren blev ikke gemt.');
          }
        },
      })
      .add(() => {
        this.saving = false;
      });
  }

  public cancel() {
    this.dialogRef.close();
  }

  public getErrorMessage(controlName: string) {
    switch (controlName) {
      case 'emailAddress':
        return 'Indtast venligst en valid email adresse';
      case 'name':
        return 'Indtast venligst navn';
      case 'selectedRole':
        return 'VÃ¦lg venligst en rolle';
      default:
        return '';
    }
  }
}
